import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { setAllSourcesToProprietary } from 'src/helpers/formHelpers';
import { actionsNotifications } from 'src/pages/app/_BLL/notifications/slice';
import { Company, DashBoardMenuName, MetaEntity, SingleMenuItem } from 'src/pages/CompanyPage/_BLL/types';
import { apiRequest } from 'src/shared/api/api';
import { RequestStatus, UrlAPI } from 'src/shared/api/types';
import { generateMenuItems } from './lib/generateMenuItems';

const NAME = 'company';

const getMeta = createAsyncThunk(`${NAME}/getMeta`, async (_: void, thunkAPI) => {
	const res = await apiRequest.getRequest<MetaEntity>({
		url: UrlAPI.metaData,
		thunkAPI,
	});

	return {
		menuItems: generateMenuItems(res),
		meta: res,
	};
});

const getCompany = createAsyncThunk(`${NAME}/getCompany`, async (params: { id: string }, thunkAPI) => {
	const res = await apiRequest.getRequest<Company>({
		url: UrlAPI.company,
		params,
		thunkAPI,
	});

	return setAllSourcesToProprietary(res);
});

const updateCompany = createAsyncThunk(`${NAME}/updateCompany`, async (args: { payload: Company; submitForRating: boolean }, thunkAPI) => {
	const { payload, submitForRating } = args;
	const { dispatch } = thunkAPI;

	const res = await apiRequest.postRequest<Company>({
		url: UrlAPI.company,
		payload,
		thunkAPI,
	});

	if (submitForRating) {
		dispatch(actionsCompany.setFinalizing('finalizing'));

		await apiRequest.getRequest({
			url: `${UrlAPI.company}/${UrlAPI.finalize}`,
			params: {
				id: payload.id,
			},
			thunkAPI,
		});

		dispatch(actionsCompany.setFinalizing('submitting'));

		await apiRequest.getRequest({
			url: `${UrlAPI.company}/${UrlAPI.submit}`,
			params: {
				id: payload.id,
			},
			thunkAPI,
		});

		dispatch(actionsCompany.setFinalizing(null));

		dispatch(
			actionsNotifications.addNotification({
				type: 'success',
				message: 'Company data submitted',
			}),
		);
	}

	return res;
});

const refreshCompany = createAsyncThunk(`${NAME}/refreshCompany`, async (args: { params: { id: number } }, thunkAPI) => {
	const { params } = args;
	const { dispatch } = thunkAPI;

	await apiRequest.getRequest({
		url: `${UrlAPI.company}/${UrlAPI.refresh}`,
		params,
		thunkAPI,
	});

	dispatch(
		actionsNotifications.addNotification({
			type: 'success',
			message: 'Company data refreshed to latest available',
		}),
	);
});

// * Reducer
interface State {
	meta: MetaEntity | null;
	company: Company | null;
	menuItems: SingleMenuItem[];
	activeMenu: DashBoardMenuName;
	formHaveErrors: boolean;
	finalizing: null | 'submitting' | 'finalizing';
	status: RequestStatus;
}

export const initialState: State = {
	meta: null,
	company: null,
	menuItems: [],
	activeMenu: DashBoardMenuName.welcome,
	formHaveErrors: false,
	finalizing: null,
	status: RequestStatus.still,
};

export const slice = createSlice({
	name: NAME,
	initialState,
	reducers: {
		setFinalizing: (state, action: PayloadAction<null | 'submitting' | 'finalizing'>) => {
			state.finalizing = action.payload;
		},
		setActiveMenuOption: (state, action: PayloadAction<DashBoardMenuName>) => {
			state.activeMenu = action.payload;
		},
		storeProgress: (state, action: PayloadAction<{ tabName: DashBoardMenuName; progressValue: number }>) => {
			const { progressValue, tabName } = action.payload;

			if (state.menuItems) {
				const newMenuItems = state.menuItems.map((item) => {
					if (tabName === item.name) {
						return {
							name: item.name,
							value: progressValue,
							totalValue: item.totalValue,
						};
					}

					return item;
				});

				state.menuItems = newMenuItems;
			}
		},
		setFormHaveErrors: (state, action: PayloadAction<boolean>) => {
			state.formHaveErrors = action.payload;
		},
	},
	extraReducers: (builder) => {
		builder.addCase(getMeta.pending, (state) => {
			state.status = RequestStatus.loading;
		});
		builder.addCase(getMeta.fulfilled, (state, action) => {
			state.menuItems = action.payload.menuItems;
			state.meta = action.payload.meta;
			state.status = RequestStatus.still;
		});
		builder.addCase(getMeta.rejected, (state) => {
			state.status = RequestStatus.failed;
		});

		builder.addCase(getCompany.pending, (state) => {
			state.status = RequestStatus.loading;
		});
		builder.addCase(getCompany.fulfilled, (state, action) => {
			state.company = action.payload;
			state.status = RequestStatus.still;
		});
		builder.addCase(getCompany.rejected, (state) => {
			state.status = RequestStatus.failed;
		});

		builder.addCase(updateCompany.pending, (state) => {
			state.status = RequestStatus.loading;
		});
		builder.addCase(updateCompany.fulfilled, (state, action) => {
			state.company = action.payload;
			state.status = RequestStatus.still;
		});
		builder.addCase(updateCompany.rejected, (state) => {
			state.status = RequestStatus.failed;
		});

		builder.addCase(refreshCompany.pending, (state) => {
			state.status = RequestStatus.loading;
		});
		builder.addCase(refreshCompany.fulfilled, (state) => {
			state.status = RequestStatus.still;
		});
		builder.addCase(refreshCompany.rejected, (state) => {
			state.status = RequestStatus.failed;
		});
	},
});

export const actionsCompany = {
	...slice.actions,
	getMeta,
	getCompany,
	updateCompany,
	refreshCompany,
};
